import { type Modifier, type SDK_Colors, type StyleModifiersType, hideOnOptions, shadowOptions } from "@quickpoint.me/shared";
import { overImageShadow, shadow } from "../css/shadow";

import type { PageSizeAtomType } from "../@types/viewerati";

// Font weight mapping for descriptive names to numeric values
const fontWeightMap = {
  thin: "100",
  "extra light": "200",
  light: "300",
  normal: "400",
  medium: "500",
  "semi bold": "600",
  bold: "700",
  "extra bold": "800",
  heavy: "900",
  "ultra heavy": "950",
};

// TODO: This does not catch the absence of modifiers


// Helper function to convert values to pixels if numeric
const toPixels = (value: string, baseFontSize: number): string => {
  const numValue = Number.parseFloat(value);
  return Number.isNaN(numValue) ? value : `${numValue * baseFontSize}px`;
};

// Helper function to resolve colors from filterColors or use raw value
const getColor = (value: string, filterColors: SDK_Colors): string => {
  return filterColors[value as keyof SDK_Colors] || value;
};

// Modifier functions map: each key maps to a function that generates CSS
const modifierMap: Record<StyleModifiersType, (params: ModifierParams) => string> = {
  "across:": ({ keyword, value, onePercentWidth }) => {
    if (!onePercentWidth) return "";
    let css = `position: absolute; left: ${toPixels(value, onePercentWidth)};`;
    if (keyword === "image:") css += "height: unset;";
    return css;
  },

  "align:": ({ keyword, value }) => {
    const positionMap = {
      across: "unset",
      bottom: "end",
      center: "center",
      down: "unset",
      left: "start",
      right: "end",
      top: "start",
    };
    const posValue = positionMap[value as keyof typeof positionMap] || value;
    // the --qp-el-* variables override the stacked-page Tufte defaults in
    // DisplayElementsChildren, which would otherwise win on specificity
    if (keyword === "image:") return `justify-self: ${posValue}; align-items: ${posValue}; --qp-el-justify: ${posValue};`;
    return `text-align: ${value}; justify-self: ${posValue}; justify-content: ${posValue}; align-self: ${posValue}; --qp-el-align: ${value}; --qp-el-justify: ${posValue};`;
  },

  "anchor:": ({ keyword, value, isInFlexBox }) => {
    const positionMap = {
      across: "unset",
      bottom: "end",
      center: "center",
      down: "unset",
      left: "flex-start",
      right: "flex-end",
      top: "start",
    };
    const posValue = positionMap[value as keyof typeof positionMap] || value;
    if (keyword === "image:") return `justify-content: ${posValue};`;
    if (isInFlexBox) return `align-self: ${posValue};`;
    return `align-self: ${posValue};`;
  },

  "animate:": () => {
    return ""
  },

  "animateStart:": () => {
    return ""
  },

  "background:": ({ value, filterColors }) => {
    const color = getColor(value, filterColors);
    return `background-color: ${color};`;
  },

  "border-color:": ({ value, filterColors }) => {
    const color = getColor(value, filterColors);
    return `border-style: solid; border-color: ${color};`;
  },

  "border-thickness:": ({ value, baseFontSizeRaw }) => {
    const pixelValue = toPixels(value, baseFontSizeRaw);
    return `border-style: solid; border-width: ${pixelValue};`;
  },

  "blur:": ({ keyword, value }) => {
    if (!value) return "filter: none;";
    const blurValue = Number.parseInt(value) / 20;
    return keyword === "box:" ? `backdrop-filter: blur(${blurValue}rem);` : `filter: blur(${blurValue}rem);`;
  },

  "color:": ({ keyword, value, filterColors }) => {
    const color = getColor(value, filterColors);
    if (keyword === "line:") {
      return `
        .hr-text {
          &:before {
            background: linear-gradient(to right, transparent, ${color}, transparent);
          }
          &:after {
            color: ${color};
          }
        }
      `;
    }
    return `color: ${color};`;
  },

  "columns:": ({ value, isInPortrait }) => `column-count: ${isInPortrait ? 1 : value};`,

  "corner-radius:": ({ value, baseFontSizeRaw }) => `border-radius: ${toPixels(value, baseFontSizeRaw)};`,

  "decoration:": ({ value }) => `text-decoration-line: ${value};`,

  "down:": ({ keyword, value, onePercentHeight }) => {
    if (!onePercentHeight) return "";
    let css = `position: absolute; top:  ${toPixels(value, onePercentHeight)};`;
    if (keyword === "image:") css += "height: unset;";
    return css;
  },

  "font:": ({ value }) => `font-family: ${value};`,

  "height:": ({ value, onePercentHeight }) =>
    `height:  ${toPixels(value, onePercentHeight)};` + `max-height:  ${toPixels(value, onePercentHeight)};`,

  "hideOn:": ({ value, pageSize }) => {
    if (value === hideOnOptions[0] && pageSize.landscape) return "display: none;";
    if (value === hideOnOptions[1] && !pageSize.landscape) return "display: none;";
    return "";
  },

  "line-height:": ({ value, baseFontSize }) => `line-height: ${toPixels(value, baseFontSize)};`,

  "opacity:": ({ keyword, value }) => {
    if (!value) return "opacity: unset;";
    const opacityValue = Number.parseInt(value) / 100;
    return keyword === "box:" ? "" : `opacity: ${opacityValue};`;
  },

  "padding:": ({ value, baseFontSizeRaw }) => `padding: ${toPixels(value, baseFontSizeRaw)};`,

  "padding-bottom:": ({ value, baseFontSizeRaw }) => `padding-block-end: ${toPixels(value, baseFontSizeRaw)};`,

  "padding-left:": ({ value, baseFontSizeRaw }) => `padding-inline-start: ${toPixels(value, baseFontSizeRaw)};`,

  "padding-right:": ({ value, baseFontSizeRaw }) => `padding-inline-end: ${toPixels(value, baseFontSizeRaw)};`,

  "padding-top:": ({ value, baseFontSizeRaw }) => `padding-block-start: ${toPixels(value, baseFontSizeRaw)};`,

  "shadow:": ({ value }) => (value === shadowOptions[1] ? overImageShadow() : shadow()),

  "size:": ({ keyword, value, baseFontSize }) => {
    const propertyMap = {
      "*:": "font-size",
      "background:": "background-size",
      "box:": "font-size",
      "paragraph:": "font-size",
      "subtitle:": "font-size",
      "supertitle:": "font-size",
      "title:": "font-size",
    };
    const property = propertyMap[keyword as keyof typeof propertyMap] || "font-size";
    return `${property}: ${toPixels(value, baseFontSize)};`;
  },


  "weight:": ({ value }) => `font-weight: ${fontWeightMap[value as keyof typeof fontWeightMap] || "400"};`,

  "width:": ({ value, onePercentWidth }) =>
    `width:${toPixels(value, onePercentWidth)};` +
    `max-width:${toPixels(value, onePercentWidth)};` +
    `--qp-el-max-width:${toPixels(value, onePercentWidth)};`,
};

// Type definition for modifier parameters
interface ModifierParams {
  keyword: string;
  value: string;
  baseFontSize: number;
  baseFontSizeRaw: number;
  filterColors: SDK_Colors;
  pageSize: PageSizeAtomType;
  onePercentHeight: number;
  onePercentWidth: number;
  isInFlexBox: boolean;
  isInPortrait: boolean;
}

// Main parsing function
const parseModifier = ({
  keyword,
  modifier,
  baseFontSize,
  baseFontSizeRaw,
  filterColors,
  pageSize,
  onePercentHeight,
  onePercentWidth,
  isInFlexBox,
  isInPortrait,
}: {
  keyword: string;
  modifier?: Modifier;
  baseFontSize: number;
  baseFontSizeRaw: number;
  filterColors: SDK_Colors;
  pageSize: PageSizeAtomType;
  onePercentHeight: number;
  onePercentWidth: number;
  isInFlexBox: boolean;
  isInPortrait: boolean;
}): string => {
  if (!modifier) return "";
  let cssString = "";
  for (const [key, value] of Object.entries(modifier)) {
    if (typeof value !== "string") continue;
    const modifierFunc = modifierMap[key as StyleModifiersType];
    if (modifierFunc) {
      cssString += modifierFunc({ keyword, value, baseFontSize, baseFontSizeRaw, filterColors, pageSize, onePercentHeight, onePercentWidth, isInFlexBox, isInPortrait });
    }
  }
  return cssString;
};

// Exported styled modifier function
const modifierStyled = (
  keyword: string,
  {
    $modifier,
    $baseFontSize,
    $baseFontSizeRaw,
    $filterColors,
    $pageSize,
    $onePercentHeight,
    $onePercentWidth,
    $isInFlexBox,
    $isInPortrait,
  }: {
    $modifier?: Modifier;
    $baseFontSize: number;
    $baseFontSizeRaw: number;
    $filterColors: SDK_Colors;
    $pageSize: PageSizeAtomType;
    $onePercentHeight: number;
    $onePercentWidth: number;
    $isInFlexBox: boolean;
    $isInPortrait: boolean;
  }
) =>
  parseModifier({
    keyword,
    modifier: $modifier,
    baseFontSize: $baseFontSize,
    baseFontSizeRaw: $baseFontSizeRaw,
    filterColors: $filterColors,
    pageSize: $pageSize,
    onePercentHeight: $onePercentHeight,
    onePercentWidth: $onePercentWidth,
    isInFlexBox: $isInFlexBox,
    isInPortrait: $isInPortrait,
  });

export default modifierStyled;
